import {
  Box,
  IconButton,
  useTheme,
  Card,
  Icon,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import AppButton from "../buttons/AppButton";
import CustomTypography from "../form/CustomTypography";
import { useState } from "react";
import { OrderServices } from "../../../../models/Order/OrderProduct";
import { observer } from "mobx-react";
import pencil_uderline from "../../../../assets/pencil_uderline.png";
import SingleInput from "../pop-up/SingleInput";

interface OrderServiceTableProps {
  services?: OrderServices[];
  padding?: string;
  boxShadow?: string;
  updateQuantity?: (
    id: number,
    custom_name: string,
    value: number,
    field: string,
  ) => void;
  handleDeleteService: (id: number, custom_name: string) => void;
  toggleServicesPopup: () => void;
  canUpdateOfferPrice?: boolean;
  canUpdateSellingPrice?: boolean;
  canChangeQuantity?: boolean;
  canDeleteService?: boolean;
  canAddService?: boolean;
  canSeeTaxes?: boolean;
  canSeeAmount?: boolean;
  canShowMrp?: boolean;
  isSez?: boolean;
  validationErrorBackground?: boolean;
}

interface MetricCellProps {
  label: string;
  value: React.ReactNode;
  onEdit?: () => void;
}

const MetricCell: React.FC<MetricCellProps> = ({ label, value, onEdit }) => (
  <Box
    sx={{ display: "flex", flexDirection: "column", gap: 0.25, minWidth: 68 }}
  >
    <Box
      component="span"
      sx={{
        fontSize: "0.63rem",
        color: "#94A3B8",
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.04em",
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </Box>
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
      <Box
        component="span"
        sx={{ fontSize: "0.82rem", fontWeight: 600, color: "#334155" }}
      >
        {value}
      </Box>
      {onEdit && (
        <IconButton
          size="small"
          onClick={onEdit}
          sx={{ width: 16, height: 16, p: 0.25 }}
        >
          <Icon sx={{ fontSize: "12px" }}>
            <img
              src={pencil_uderline}
              alt="edit-icon"
              style={{ width: 10, height: 10 }}
            />
          </Icon>
        </IconButton>
      )}
    </Box>
  </Box>
);

const OrderServiceTable: React.FC<OrderServiceTableProps> = ({
  services,
  padding,
  boxShadow,
  updateQuantity,
  handleDeleteService,
  toggleServicesPopup,
  canUpdateOfferPrice = true,
  canUpdateSellingPrice = true,
  canChangeQuantity = true,
  canDeleteService = true,
  canAddService = true,
  canSeeTaxes = true,
  canSeeAmount = true,
  canShowMrp = true,
  isSez = false,
  validationErrorBackground = false,
}) => {
  const theme = useTheme();
  const [editableField, setEditableField] = useState<{
    productId: number | undefined;
    custom_name: string | undefined;
    field: string | undefined;
  }>({
    productId: undefined,
    custom_name: undefined,
    field: undefined,
  });

  const [modalOpen, setModalOpen] = useState(false);
  const [modalValue, setModalValue] = useState("");

  const handleEditClick = (
    productId: number,
    custom_name: string,
    field: string,
    currentValue: string,
  ) => {
    setEditableField({ productId, custom_name, field });
    setModalValue(currentValue);
    setModalOpen(true);
  };

  const handleModalSubmit = (value: string) => {
    if (!editableField.productId || !editableField.field) return;

    if (editableField.field) {
      updateField(
        editableField.productId,
        editableField.custom_name,
        Number(value),
        editableField.field,
      );
    }

    setModalOpen(false);
    setEditableField({
      productId: undefined,
      custom_name: undefined,
      field: undefined,
    });
  };

  const updateField = (
    productId: number,
    custom_name: string | undefined,
    value: number,
    field: string,
  ) => {
    if (updateQuantity)
      updateQuantity(productId, custom_name || "", value, field);
  };

  const formatFieldName = (fieldName: string): string => {
    return fieldName
      .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
      .replace(/^./, (str) => str.toUpperCase());
  };

  return (
    <Box sx={{ borderRadius: 2 }}>
      <Card
        sx={{
          p: padding || 5,
          boxShadow:
            boxShadow ||
            "rgba(9, 30, 66, 0.25) 0px 1px 1px, rgba(9, 30, 66, 0.13) 0px 0px 1px 1px",
          ...(validationErrorBackground && {
            border: `1px solid ${theme.palette.error.main}`,
          }),
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <CustomTypography variant="heading4" fontWeight={700}>
            Services
          </CustomTypography>
          {canAddService && (
            <AppButton
              label="Add Service"
              startIcon={<AddIcon />}
              onClick={toggleServicesPopup}
              customStyles={{
                backgroundColor: "transparent",
                padding: 1,
                margin: 0,
                fontSize: "14px",
                color: theme.palette.customColors.pinkDark,
                "&:hover": {
                  backgroundColor: "transparent",
                  color: theme.palette.customColors.pinkDark,
                  boxShadow: "none",
                },
              }}
            />
          )}
        </Box>

        {!services || services.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 3 }}>
            <CustomTypography
              variant="body2"
              fontWeight={700}
              color="rgba(218, 218, 218, 1)"
            >
              NO SERVICES FOUND!
            </CustomTypography>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {services.map((service, index) => {
              const tax =
                canSeeTaxes && isSez === false
                  ? service.amount && service.gst_percentage
                    ? (
                        (service.amount * service.gst_percentage) /
                        100
                      ).toFixed(2)
                    : "--"
                  : "0";

              return (
                <Box
                  key={index}
                  sx={{
                    border: "1px solid #E2E8F0",
                    borderLeft: `3px solid ${theme.palette.primary.main}`,
                    borderRadius: "10px",
                    overflow: "hidden",
                    backgroundColor: "#fff",
                  }}
                >
                  {/* Header row */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      px: 2,
                      py: 1.25,
                      flexWrap: "wrap",
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        fontSize: "0.72rem",
                        color: "#94A3B8",
                        fontWeight: 700,
                        minWidth: 24,
                      }}
                    >
                      {index + 1}.
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 100 }}>
                      <CustomTypography variant="body2" fontWeight={700}>
                        {service.name || "--"}
                      </CustomTypography>
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                          mt: 0.25,
                          flexWrap: "wrap",
                          alignItems: "center",
                        }}
                      >
                        <Box
                          component="span"
                          sx={{
                            fontSize: "0.7rem",
                            color: "#94A3B8",
                            backgroundColor: "#F1F5F9",
                            borderRadius: "4px",
                            px: 0.75,
                            py: 0.25,
                            fontWeight: 600,
                          }}
                        >
                          #{service.id}
                        </Box>
                        {service.custom_name && (
                          <CustomTypography variant="body3" color="#64748B">
                            {service.custom_name}
                          </CustomTypography>
                        )}
                        {service.sku && (
                          <CustomTypography variant="body3" color="#94A3B8">
                            SKU: {service.sku}
                          </CustomTypography>
                        )}
                        {service.sac_code && (
                          <CustomTypography variant="body3" color="#94A3B8">
                            SAC: {service.sac_code}
                          </CustomTypography>
                        )}
                      </Box>
                    </Box>
                    {canDeleteService && (
                      <IconButton
                        size="small"
                        sx={{
                          backgroundColor:
                            theme.palette.customColors.pinkLight,
                          width: 28,
                          height: 28,
                          "&:hover": {
                            backgroundColor:
                              theme.palette.customColors.pinkLight + "cc",
                          },
                        }}
                        onClick={() =>
                          handleDeleteService(
                            service.id,
                            service.custom_name || "",
                          )
                        }
                      >
                        <DeleteIcon
                          sx={{
                            color: theme.palette.customColors.pinkDark,
                            width: 16,
                            height: 16,
                          }}
                        />
                      </IconButton>
                    )}
                  </Box>

                  {/* Metrics strip */}
                  <Box
                    sx={{
                      px: 2,
                      py: 1,
                      backgroundColor: "#FAFAFA",
                      borderTop: "1px solid #F1F5F9",
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 3,
                    }}
                  >
                    <MetricCell
                      label="Orig Price"
                      value={service.service_selling_price || "--"}
                    />
                    <MetricCell
                      label="Offer Price"
                      value={service.unit_price}
                      onEdit={
                        canUpdateOfferPrice
                          ? () =>
                              handleEditClick(
                                service.id,
                                service.custom_name ?? "",
                                "offerPrice",
                                String(service.unit_price),
                              )
                          : undefined
                      }
                    />
                    <MetricCell
                      label="Sell Price"
                      value={service.selling_price}
                    />
                    <MetricCell
                      label="Quantity"
                      value={service.quantity}
                      onEdit={
                        canChangeQuantity
                          ? () =>
                              handleEditClick(
                                service.id,
                                service.custom_name ?? "",
                                "quantity",
                                String(service.quantity),
                              )
                          : undefined
                      }
                    />
                    <MetricCell
                      label="GST%"
                      value={service.gst_percentage || "--"}
                    />
                    <MetricCell label="Tax" value={tax} />
                    {canShowMrp && (
                      <MetricCell label="MRP" value={service.mrp || "--"} />
                    )}
                    {canSeeAmount && (
                      <MetricCell
                        label="Amount"
                        value={
                          Math.round(
                            (service.amount + Number.EPSILON) * 100,
                          ) /
                            100 || "--"
                        }
                      />
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}

        <SingleInput
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          heading={`Edit ${
            editableField.field ? formatFieldName(editableField.field) : ""
          }`}
          content={`Enter value`}
          optionalText={`${
            editableField.productId
              ? services?.find(
                  (service) => service.id === editableField.productId,
                )?.name
              : ""
          }`}
          type="number"
          initialValue={modalValue}
          onSubmit={handleModalSubmit}
        />
      </Card>
    </Box>
  );
};

export default observer(OrderServiceTable);
